/**
 * L'ARCHITECTURE DE NAVIGATION DU BACK-OFFICE — la donnée, séparée du rendu.
 *
 * UTILITÉ : le rail se lit ici plutôt que dans le JSX, parce que ce n'est pas une liste de liens mais
 * une DÉCISION PRODUIT — l'ordre suit la fréquence d'usage réelle d'un commerçant (CADRAGE §4, arrêté
 * le 2026-07-30), pas la logique de construction du logiciel : on consulte ses ventes tous les jours,
 * son catalogue souvent, son stock régulièrement, ses clients moins, ses réglages presque jamais.
 * Réordonner le rail doit donc être une décision qu'on lit, pas un déplacement de balises.
 */

/** Une entrée de rail : soit une destination, soit un groupe qui en contient. */
import type { IconName } from '@orgazone/ui-tokens';
// ⚠️ `key` DÉCLARE, elle ne traduit pas : cette table est évaluée au CHARGEMENT du module, donc un
//    `t()` y figerait le français avant que la langue du compte n'arrive. Les libellés restent donc
//    lisibles ici — le rail, la palette et les routes les traduisent AU RENDU.
import { key, type Translatable } from './i18n';
export interface NavLeaf {
  path: string;
  label: Translatable;
  /** Droit requis. Absent = visible par tous les rôles ayant accès au back-office. */
  permission?: string;
}

export interface NavGroup {
  id: string;
  label: Translatable;
  icon: IconName;
  children: NavLeaf[];
}

export type NavEntry = ({ kind: 'leaf' } & NavLeaf & { icon: IconName }) | ({ kind: 'group' } & NavGroup);

/**
 * Les entrées de premier niveau.
 *
 * ⚠️ « Tableau de bord » est une LIGNE, jamais un groupe : c'est la porte d'entrée quotidienne, elle se
 * gagne en un clic. Une première version regroupait Tableau de bord, Santé NF525 et Statistiques sous
 * une rubrique « Pilotage » — abandonnée le jour même : les trois n'ont ni le même usage, ni le même
 * public, ni les mêmes droits.
 *
 * ⚠️ « Statistiques » EST ICI depuis le 2026-08-16, et son absence était une ERREUR DE MA PART. Je
 * l'avais retirée au nom du §23.0 (« pas d'option non implémentée ») — mais l'argument ne tient pas :
 * les TRENTE autres entrées de ce rail mènent elles aussi à un écran d'attente qui dit ce qui n'est
 * pas construit. Exclure celle-là seule n'était pas une règle appliquée, c'était une exception
 * arbitraire — et elle faisait diverger le menu de la maquette, qui dessine ce groupe et ses sept axes.
 * Le §23.0 interdit de faire SEMBLANT, pas d'annoncer une rubrique dont l'écran dit franchement
 * qu'il reste à construire.
 *
 * ⚠️ « Santé NF525 » n'est pas non plus dans cette liste : c'est un DROIT, pas une rubrique. Le porter
 * dans un groupe reviendrait à le banaliser ; il vit isolé en pied de rail (voir `RESTRICTED`), et
 * reste INVISIBLE — pas grisé — pour qui ne l'a pas.
 */
export const NAV: NavEntry[] = [
  { kind: 'leaf', path: '/', label: key('Tableau de bord'), icon: 'grid' },
  // ⚠️ LE CENTRE D'ÉCARTS EST UNE LIGNE DE PREMIER NIVEAU, pas une entrée du groupe Stock (ordre
  // relevé dans la maquette, 2026-08-30). Ce n'est pas un détail de rangement : un écart est une
  // ALERTE qui attend un arbitrage humain — l'enterrer dans un accordéon replié le rend invisible
  // le jour où il compte. La maquette lui donne d'ailleurs le triangle d'alerte, pas l'icône stock.
  { kind: 'leaf', path: '/ecarts', label: key('Centre d’écarts'), icon: 'alert' },
  {
    kind: 'group',
    id: 'ventes',
    label: key('Ventes & fiscal'),
    icon: 'receipt',
    children: [
      { path: '/journal', label: key('Journal des ventes') },
      { path: '/objectifs', label: key('Objectifs') },
      { path: '/cloture', label: key('Z & clôtures') },
      { path: '/export-compta', label: key('Export comptable') },
      { path: '/archives', label: key('Archives fiscales') },
    ],
  },
  {
    kind: 'group',
    id: 'catalogue',
    label: key('Catalogue & prix'),
    icon: 'tag',
    children: [
      { path: '/articles', label: key('Articles') },
      { path: '/articles-rapides', label: key('Articles rapides') },
      { path: '/etiquettes', label: key('Étiquettes') },
      // Libellé de la maquette : « À valider » — le groupe dit déjà qu'il s'agit d'articles.
      { path: '/a-valider', label: key('À valider') },
      { path: '/prix', label: key('Prix & marges') },
      { path: '/promotions', label: key('Promotions & soldes') },
    ],
  },
  {
    kind: 'group',
    id: 'stock',
    label: key('Stock'),
    icon: 'box',
    children: [
      { path: '/stock', label: key('État du stock') },
      { path: '/mouvements', label: key('Mouvements') },
      { path: '/receptions', label: key('Commandes & réceptions') },
      { path: '/retrocessions', label: key('Rétrocessions') },
      { path: '/inventaires', label: key('Inventaires') },
    ],
  },
  {
    kind: 'group',
    id: 'clients',
    label: key('Clients'),
    icon: 'users',
    children: [
      { path: '/clients', label: key('Fichier clients') },
      { path: '/titres', label: key('Titres & bons') },
      // ⚠️ DANS « Clients » et non « Ventes & fiscal » : une échéance n'est PAS une pièce — rien n'y
      // est signé ni encaissé, c'est une annonce du client. Elle répond à « qui doit quoi », qui est
      // une question de fichier clients.
      { path: '/relances', label: key('Échéances & relances') },
      { path: '/marketing', label: key('Marketing') },
    ],
  },
  {
    // ⚠️ Sept axes, exactement ceux de la maquette. Le cadrage les dit « à cadrer » : c'est l'écran
    // d'attente qui le porte, pas l'absence de la rubrique.
    kind: 'group',
    id: 'stats',
    label: key('Statistiques'),
    icon: 'chart',
    children: [
      { path: '/stats/ventes', label: key('Ventes') },
      { path: '/stats/stock', label: key('Stock') },
      { path: '/stats/vendeurs', label: key('Vendeurs') },
      { path: '/stats/articles', label: key('Articles') },
      { path: '/stats/marges', label: key('Marges & achats') },
      { path: '/stats/clients', label: key('Clients') },
      { path: '/stats/fidelite', label: key('Fidélité & titres') },
      { path: '/stats/marketing', label: key('Marketing') },
    ],
  },
  {
    kind: 'group',
    id: 'admin',
    label: key('Réglages'),
    icon: 'cog',
    // ⚠️ L'ORDRE EST CELUI DU RAIL DE LA MAQUETTE (garde `nav-ordre-maquette.test.ts`) ; Santé NF525 vit au pied du rail
    //    (`RESTRICTED`).
    children: [
      { path: '/operateurs', label: key('Opérateurs & droits') },
      { path: '/acces', label: key('Accès back-office') },
      // ⚠️ LE VOCABULAIRE EST UN ONGLET DES RÉFÉRENTIELS (`?tab=vocab`), comme dans la maquette : un mot n'est pas un
      // réglage de caisse, c'est une liste de l'enseigne. Les 138 clés `vocab*` du catalogue restent hors des onglets
      // de Paramètres — la garde `parametres-modele.test.ts` les en écarte par le contrat.
      { path: '/referentiels', label: key('Référentiels') },
      // ⚠️ LE PARC DE PÉRIPHÉRIQUES ET LA MESSAGERIE DU MAGASIN VIVENT DANS PARAMÈTRES (onglets Périphériques et
      // Mail & SMTP), sans entrée ici : ce sont des réglages du MAGASIN (décision utilisateur 2026-10-03).
      { path: '/parametres', label: key('Paramètres') },
      // ⚠️ « Fidélité » est un RÉGLAGE (paliers, taux, validités), pas une rubrique du fichier
      // clients : la maquette le range ici, et c'est cohérent — on règle le programme une fois,
      // on consulte les clients tous les jours.
      { path: '/fidelite', label: key('Fidélité') },
      { path: '/integrations', label: key('Intégrations') },
      // ⚠️ DANS « Réglages » ET NON DANS « Marketing » : un gabarit sert AUSSI au courrier de facture.
      // Le ranger sous Marketing le rendrait invisible à qui cherche le texte d’un document.
      { path: '/gabarits-email', label: key('Gabarits d’e-mail') },
      { path: '/magasin', label: key('Magasin & caisses') },
      // ⚠️ DESSINÉ « JOURNAL D'AUDIT » : ce n'est pas un audit — la preuve fiscale vit dans Santé NF525 et les Archives.
      //    Il montre l'activité du magasin, aux caisses et au siège (CADRAGE-Journal-Activite.md).
      { path: '/activite', label: key('Journal d’activité') },
    ],
  },
];

/**
 * Le pied de rail « Accès restreint ».
 *
 * ⚠️ Isolé, et MASQUÉ hors droit — jamais grisé. Un poste de surveillance de conformité qu'on montre
 * sans pouvoir l'ouvrir n'informe personne et invite à demander pourquoi ; l'absence, elle, ne ment pas.
 * Corollaire tenu ailleurs : aucun écran ne renvoie vers un module que le rôle ne voit pas.
 */
export const RESTRICTED: NavLeaf[] = [
  { path: '/sante-nf525', label: key('Santé NF525'), permission: 'bo.nf525.view' },
];

/** L'écran du compte — ouvert depuis le pied du rail (clic sur son nom), jamais depuis le menu. */
export const ACCOUNT_PATH = '/mon-compte';

/**
 * LES DESTINATIONS SANS ENTRÉE DE MENU — ouvertes depuis un autre endroit du rail.
 * ⚠️ DÉCLARÉES ICI, avec les autres : le routeur et la palette ne connaissent que ce que ce module déclare, et un écran
 *    retiré du menu ne doit pas cesser d'exister avec son entrée (il répondrait « cette adresse n'existe pas »).
 */
export const OFF_MENU: NavLeaf[] = [
  // « Mon compte » : ce qu'on y règle ne vaut que pour SOI — langue, mot de passe, sessions —, là où le groupe
  // « Réglages » règle le MAGASIN. La maquette met donc la porte sur le nom, au pied du rail.
  { path: ACCOUNT_PATH, label: key('Mon compte') },
];

/** Le groupe qui contient un chemin — sert à ouvrir le bon accordéon à l'arrivée sur une page. */
export function groupOf(path: string): string | null {
  for (const e of NAV) {
    if (e.kind === 'group' && e.children.some((c) => c.path === path)) return e.id;
  }
  return null;
}

/** Le libellé d'une destination, pour les titres de page et le fil d'Ariane. */
export function labelOf(path: string): Translatable | null {
  for (const e of NAV) {
    if (e.kind === 'leaf' && e.path === path) return e.label;
    if (e.kind === 'group') {
      const hit = e.children.find((c) => c.path === path);
      if (hit) return hit.label;
    }
  }
  return RESTRICTED.find((r) => r.path === path)?.label ?? OFF_MENU.find((r) => r.path === path)?.label ?? null;
}
